import React, { useEffect, useState } from 'react';
import { Loader2, GraduationCap } from 'lucide-react';
import { atlasApi, AtlasUser, Treinamento, TreinamentoInscricao } from '../shared/atlasApi';
import BadgeStatusT from './BadgeStatusT';

interface Props {
  user: AtlasUser;
}

interface Item { inscricao: TreinamentoInscricao; treinamento: Treinamento | null; }

function fdata(s?: string | null) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
}

const STATUS_INSC: Record<string, { label: string; classes: string }> = {
  inscrito: { label: 'Inscrito', classes: 'bg-blue-100 text-blue-700' },
  presente: { label: 'Presente', classes: 'bg-emerald-100 text-emerald-700' },
  ausente: { label: 'Ausente', classes: 'bg-rose-100 text-rose-700' },
  concluido: { label: 'Concluído', classes: 'bg-purple-100 text-purple-700' },
  cancelado: { label: 'Cancelado', classes: 'bg-gray-100 text-gray-500' },
};

const MeusTreinamentos: React.FC<Props> = ({ user }) => {
  const [itens, setItens] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    // Resolve o próprio funccodi via /disc/meu (reusa lookup por email) e busca treinamentos
    atlasApi.discMeu(user)
      .then(me => {
        if (me.funccodi == null) throw new Error('Você não possui registro de funcionário CLT.');
        return atlasApi.treinamentosColaborador(user, me.funccodi);
      })
      .then(r => { if (!cancel) setItens(r.itens || []); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user]);

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>;
  if (erro) return <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-amber-700">{erro}</div>;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <GraduationCap className="w-4 h-4 text-purple-600" />
        <h3 className="font-bold text-gray-900 text-sm">Meus treinamentos</h3>
      </div>
      {itens.length === 0 ? (
        <div className="p-8 text-center text-gray-400 text-sm">Você ainda não tem treinamentos.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {itens.map((it, i) => (
            <li key={i} className="px-4 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{it.treinamento?.titulo || 'Treinamento removido'}</div>
                <div className="text-xs text-gray-400">
                  {it.treinamento?.categoria || '—'} · início {fdata(it.treinamento?.data_inicio)}
                </div>
              </div>
              {it.treinamento && <BadgeStatusT status={it.treinamento.status} />}
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${(STATUS_INSC[it.inscricao.status] || { classes: 'bg-gray-100 text-gray-500' }).classes}`}>
                {(STATUS_INSC[it.inscricao.status] || { label: it.inscricao.status }).label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MeusTreinamentos;
